import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./main-form.module.scss";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import Dropdown from "../../../ui-kit/dropdown/dropdown";
import Combobox from "../../../ui-kit/combobox/combobox";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import {
  fetchMasterPlanModificationData,
  FormationTypeNames,
  selectFilteredData,
  setDrafter,
  setFilter,
  setKrookiePlots,
  setSelectedCity,
  setSelectedFormationType,
  setSelectedPlots,
} from "../../../../services/reducers/master-plan-modification";
import Button from "../../../ui-kit/button/button";
import { useNavigate } from "react-router-dom";
import { getKrookieFormationTypes } from "../../../../services/api";
import CustomScrollbar from "../../../ui-kit/scrollbar/scrollbar";
import ClosedFilterCard from "../../filters/closed-filter-card/closed-filter-card";
import { useCardNavigation } from "../../../../utils/cardNavigation";
import Input from "../../../ui-kit/Input/input";
import { MasterPlanContext } from "../master-plan-modification-portal";
import { setLayerVisibilityByEstateEn } from "../../../../services/actions/master-plan-modification";
import { LayersDataTypes } from "../../../../types/layers-data-types";
import {
  setEnableDrawingTools,
  setLayersVisible,
  setUserCanEditAttributes,
} from "../../../../services/reducers/map";
import { extentEstateEn } from "../../../../services/actions/map";
import {
  createMergedKrookiePlot,
  getWkidSelectedCity,
} from "../../../../utils/krookie-helpers";

type FormationTypes = Array<{
  name: FormationTypeNames;
  id: number;
}>;

const MainFormBlock = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((store) => store.user.user);
  const navigate = useNavigate();

  const [value, setValue] = useState<string>("");
  const [formationTypes, setFormationTypes] = useState<FormationTypes>();
  const prevCityRef = useRef<number | null>(null);
  const { closeCurrentCard } = useCardNavigation();

  const modalContext = useContext(MasterPlanContext);

  const { selectedPlots, selectedFormationType, citiesEn, citiesAr, drafter } =
    useAppSelector((store) => store.masterPlanModification);
  const selectedCity =
    useAppSelector((state) => state.masterPlanModification.selectedCity) ??
    null;

  const filteredData = useAppSelector(selectFilteredData);

  // Use selectors
  const { layers, mapComponentReady, layersIds, visibleLayersId } =
    useAppSelector((state) => state.map);

  // Dispatches
  useEffect(() => {
    if (layers && layers.length > 0) {
      dispatch(fetchMasterPlanModificationData());
    }
  }, [dispatch, layers]);
  useEffect(() => {
    if (selectedCity) {
      dispatch(setFilter(selectedCity));
    }
  }, [selectedCity]);

  useEffect(() => {
    getKrookie();
    dispatch(setUserCanEditAttributes(true));

    return () => {
      dispatch(setUserCanEditAttributes(false));
    };
  }, []);

  useEffect(() => {
    dispatch(setDrafter({ name: user?.userName || "", date: new Date() }));
  }, [user]);

  const filteredCityIds = user?.cities?.length
    ? Array.from(citiesEn.keys()).filter(key => user.cities!.includes(key))
    : Array.from(citiesEn.keys());
  const filteredCitiesEn = new Map(filteredCityIds.map(id => [id, citiesEn.get(id)!]));
  const filteredCitiesAr = new Map(filteredCityIds.map(id => [id, citiesAr.get(id)!]));

  useEffect(() => {
    if (mapComponentReady === "masterPlan") {
      if (selectedCity) {
        dispatch(setLayerVisibilityByEstateEn({ estateIds: [selectedCity] }));
      } else {
        dispatch(setLayerVisibilityByEstateEn({ estateIds: filteredCityIds }));
      }
    }
  }, [selectedCity, mapComponentReady, filteredCitiesEn]);

  useEffect(() => {
    const cityEntries = Array.from(filteredCitiesEn.entries());
    const isOnlyOneCity = cityEntries.length === 1;

    if (isOnlyOneCity && mapComponentReady === "masterPlan") {
      const [city] = cityEntries[0];
      dispatch(setSelectedCity(city));
    }
  }, [filteredCitiesEn, selectedCity, mapComponentReady]);

  function getCurrentDate(date: Date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  const checkButtonIsDisabled = () => {
    if (
      selectedFormationType === FormationTypeNames.Merge &&
      selectedPlots.length > 1
    ) {
      return false;
    } else if (
      selectedFormationType === FormationTypeNames.Edit &&
      selectedPlots.length > 0
    ) {
      return false;
    } else if (
      selectedFormationType === FormationTypeNames.Create &&
      selectedCity
    ) {
      return false;
    } else if (
      selectedFormationType === FormationTypeNames.Divide &&
      selectedPlots.length > 0
    ) {
      return false;
    }

    return true;
  };

  const getKrookie = async () => {
    const data: FormationTypes = await getKrookieFormationTypes();

    setFormationTypes(data);
  };

  useEffect(() => {
    if (prevCityRef.current !== null && prevCityRef.current !== selectedCity) {
      dispatch(setSelectedPlots([]));
      dispatch(setKrookiePlots([]));
    }
    prevCityRef.current = selectedCity;
  }, [selectedCity, dispatch]);

  const handleNext = async () => {
    if (selectedPlots.length) {
      closeCurrentCard(selectedPlots[0].fid.toString());
    }

    if (selectedFormationType == FormationTypeNames.Edit) {
      dispatch(setKrookiePlots(selectedPlots));
    }

    if (selectedFormationType === FormationTypeNames.Create) {
      const cityIds = layersIds[LayersDataTypes.CityBoundary];
      const hasNoVisibleCityLayers =
        cityIds && !visibleLayersId.some((id) => cityIds.includes(id));

      if (hasNoVisibleCityLayers) {
        dispatch(setLayersVisible([...visibleLayersId, ...cityIds]));
      }

      if (selectedCity) {
        dispatch(extentEstateEn({ estateEn: selectedCity }));

        const wkid = await getWkidSelectedCity(layers, selectedCity);

        if (wkid) {
          dispatch(setKrookiePlots({ wkid }));
        }
      }

      dispatch(setEnableDrawingTools(true));
    }

    if (selectedFormationType === FormationTypeNames.Merge) {
      const mergedPlots = createMergedKrookiePlot(selectedPlots);
      if (mergedPlots) {
        dispatch(setKrookiePlots([mergedPlots]));
      }
    }

    if (selectedFormationType === FormationTypeNames.Divide) {
      const wkid = selectedPlots[0].geometry.spatialReference.wkid;
      if (wkid && selectedCity) {
        dispatch(setKrookiePlots({ wkid, count: 2 }));
      }
      dispatch(setEnableDrawingTools(true));
    }
    navigate(`${selectedFormationType?.toLowerCase()}`);
  };

  const handleResetType = () => {
    dispatch(setSelectedFormationType(null));
    dispatch(setSelectedCity(null));
    dispatch(setSelectedPlots([]));
    dispatch(setKrookiePlots([]));
  };

  return (
    <>
      <div className={styles.form}>
        <div className={styles.groupInput}>
          <label className={`${styles.field} ${styles.name}`}>
            <span className={`${styles.fieldName}`}>
              {t("master-plan-modification.drafter")}:
            </span>
            <Input value={drafter?.name} />
          </label>
          <label className={`${styles.field} ${styles.date}`}>
            <span className={`${styles.fieldName}`}>
              {t("master-plan-modification.date")}:
            </span>
            <Input value={getCurrentDate(drafter?.date || new Date())} />
          </label>
        </div>
        <label className={`${styles.field} ${styles.typeOfFormation}`}>
          <span className={`${styles.fieldName}`}>
            {t("master-plan-modification.formationType")}:
          </span>
          <Dropdown
            options={
              new Map(
                formationTypes
                  ? formationTypes.map((item) => [item.name, item.name])
                  : [],
              )
            }
            mpmpValue={selectedFormationType ? selectedFormationType : ""}
            placeholder={t("master-plan-modification.formationPlaceholder")}
            canUnset={false}
            onChange={(selectedOption) => {
              if (
                Object.values(FormationTypeNames).includes(
                  selectedOption as FormationTypeNames,
                )
              ) {
                dispatch(
                  setSelectedFormationType(
                    selectedOption as FormationTypeNames,
                  ),
                );
                dispatch(setSelectedPlots([]));
                dispatch(setKrookiePlots([]));
              }
            }}
            onClear={handleResetType}
          />
        </label>
        <label className={`${styles.field} ${styles.city}`}>
          <span className={`${styles.fieldName}`}>
            {t("master-plan-modification.industrialCity")}:
          </span>
          <Dropdown
            options={i18n.language === "ar" ? filteredCitiesAr : filteredCitiesEn}
            placeholder={t("master-plan-modification.selectCity")}
            value={selectedCity ? selectedCity : ""}
            onChange={(city) => dispatch(setSelectedCity(city as number))}
            canUnset={false}
            disabled={!selectedFormationType}
            onClear={() => dispatch(setSelectedCity(null))}
          />
        </label>
        {selectedFormationType !== FormationTypeNames.Create && (
          <div className={`${styles.field} ${styles.plot}`}>
            <span
              style={{ opacity: selectedCity == null ? 0.5 : 1 }}
              className={`${styles.fieldName}`}
            >
              {selectedFormationType === FormationTypeNames.Merge
                ? t("master-plan-modification.plotsToMerge")
                : selectedFormationType === FormationTypeNames.Divide
                  ? t("master-plan-modification.plotToDivide")
                  : t("master-plan-modification.thePlot")}
            </span>
            <Combobox
              selected={selectedPlots}
              setSelected={(value) => dispatch(setSelectedPlots(value))}
              value={value}
              setValue={setValue}
              max={
                selectedFormationType === FormationTypeNames.Merge
                  ? Infinity
                  : 1
              }
              data={filteredData ? filteredData : []}
              disabled={selectedCity == null}
              placeholder={t("master-plan-modification.enterPlotNumber")}
              selectedFormationType={selectedFormationType || null}
            />
          </div>
        )}
      </div>
      <div className={styles.scrollContent}>
        <CustomScrollbar>
          <div>
            <div className={styles.resultsContainer}>
              {selectedPlots.map((plot: any, index: any) => (
                <ClosedFilterCard
                  type={plot.type}
                  key={index}
                  data={plot}
                  className={styles.resultsItem}
                  showBtns={true}
                  showInvestBtn={false}
                />
              ))}
            </div>
          </div>
        </CustomScrollbar>
      </div>
      <div className={styles.buttonSection}>
        <Button
          type="primary"
          disabled={checkButtonIsDisabled()}
          className={`${styles.button} ${styles.next}`}
          onClick={handleNext}
        >
          {t("master-plan-modification.next")}
        </Button>
        <Button
          onClick={() => modalContext?.setCloseModalVisible(true)}
          type="outline"
          className={`${styles.button} ${styles.cancel}`}
        >
          {t("master-plan-modification.cancel")}
        </Button>
      </div>
    </>
  );
};

export default MainFormBlock;
